/**
 * Voice command capture via Web Speech API + Sarvam Bulbul V3 TTS stub.
 *
 * Languages: en-IN, hi-IN, hi-en (code-mixed).
 * Local-only when SpeechRecognition is on-device (Safari 18 / Chrome 122+).
 */

type SR = SpeechRecognition;

interface VoiceCaptureResult {
  readonly text: string;
  readonly confidence: number;
  readonly lang: string;
}

export function hasSpeechRecognition(): boolean {
  return typeof window !== 'undefined' && (typeof (window as Window & { SpeechRecognition?: unknown }).SpeechRecognition !== 'undefined' || typeof (window as Window & { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition !== 'undefined');
}

function getCtor(): { new (): SR } | null {
  const w = window as Window & { SpeechRecognition?: { new (): SR }; webkitSpeechRecognition?: { new (): SR } };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function captureOnce(lang: 'en-IN' | 'hi-IN' = 'en-IN'): Promise<VoiceCaptureResult> {
  return new Promise<VoiceCaptureResult>((resolve, reject) => {
    const Ctor = getCtor();
    if (Ctor === null) {
      reject(new Error('SpeechRecognition unavailable'));
      return;
    }
    const sr = new Ctor();
    sr.lang = lang;
    sr.interimResults = false;
    sr.maxAlternatives = 1;
    sr.continuous = false;

    sr.onresult = (ev: SpeechRecognitionEvent): void => {
      const r = ev.results[0]?.[0];
      if (r === undefined) {
        reject(new Error('no result'));
        return;
      }
      resolve({ text: r.transcript, confidence: r.confidence, lang });
    };
    sr.onerror = (e: SpeechRecognitionErrorEvent): void => reject(new Error(e.error));
    sr.onend = (): void => {
      // resolve already fired or error fired
    };

    try {
      sr.start();
    } catch (e) {
      reject(e instanceof Error ? e : new Error('start failed'));
    }
  });
}

export interface SpeakOptions {
  readonly lang?: 'en-IN' | 'hi-IN';
  readonly rate?: number;
  readonly pitch?: number;
}

export function speak(text: string, opts: SpeakOptions = {}): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') {
      resolve();
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = opts.lang ?? 'en-IN';
    u.rate = opts.rate ?? 1.0;
    u.pitch = opts.pitch ?? 1.0;
    u.onend = (): void => resolve();
    u.onerror = (): void => resolve();
    window.speechSynthesis.speak(u);
  });
}

const SARVAM_TTS_URL = 'https://api.sarvam.ai/text-to-speech';

export interface SarvamTtsOptions {
  readonly text: string;
  readonly speaker?: 'meera' | 'pavithra' | 'maitreyi' | 'arvind';
  readonly lang?: 'hi-IN' | 'en-IN';
  readonly apiKey?: string;
}

export async function bulbulV3Speak(opts: SarvamTtsOptions): Promise<Blob | null> {
  if (typeof opts.apiKey !== 'string' || opts.apiKey === '') return null;
  try {
    const res = await fetch(SARVAM_TTS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'api-subscription-key': opts.apiKey },
      body: JSON.stringify({
        inputs: [opts.text],
        target_language_code: opts.lang ?? 'hi-IN',
        speaker: opts.speaker ?? 'meera',
        model: 'bulbul:v3',
      }),
    });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}
